// "A new JobQ build is ready": the one place the page keeps that state.
//
// serviceWorkerRegistration.js reports a waiting worker (notifyWaiting) and a worker taking over
// (notifyControllerChanged); components/UpdateBanner.jsx shows it; applyUpdate() is the tap.
// The tab that tapped reloads once the new worker is in control. Other tabs only get the banner
// ("updated, tap to reload"): reloading them on their own could throw away a half-filled form.

let state = { waiting: null, updated: false, dismissed: false };
const listeners = new Set();
let reloadRequested = false;
let reloadTimer = null;

function emit() {
  listeners.forEach((fn) => {
    try { fn(state); } catch (e) { /* a listener's error is its own */ }
  });
}

function set(patch) {
  state = { ...state, ...patch };
  emit();
}

export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function getUpdateState() {
  return state;
}

/** Show the banner when it has something to say and nobody hid it for this page. */
export function shouldShowBanner(s = state) {
  return !s.dismissed && (Boolean(s.waiting) || s.updated);
}

/** A new worker finished installing and is waiting for this page's go-ahead. */
export function notifyWaiting(registration) {
  const waiting = registration && registration.waiting;
  if (!waiting) return;
  set({ waiting, dismissed: false });
}

/** Another worker took over this page (this tab tapped, another tab did, or it was a first
 *  install that had no previous worker: the caller filters that case out). */
export function notifyControllerChanged() {
  if (reloadRequested) {
    reload();
    return;
  }
  set({ waiting: null, updated: true, dismissed: false });
}

function reload() {
  if (reloadTimer) { clearTimeout(reloadTimer); reloadTimer = null; }
  reloadRequested = false;
  window.location.reload();
}

/** The tap. A waiting worker is told to take over and the page reloads when it has; with
 *  nothing waiting (another tab already switched) it just reloads. */
export function applyUpdate() {
  const { waiting } = state;
  if (!waiting) {
    reload();
    return;
  }
  reloadRequested = true;
  try {
    waiting.postMessage({ type: 'SKIP_WAITING' });
  } catch (e) {
    reload();
    return;
  }
  // If the controller change never comes (the worker died), reload anyway.
  reloadTimer = setTimeout(reload, 4000);
}

export function dismissBanner() {
  set({ dismissed: true });
}

export function __resetSwUpdateForTests() {
  state = { waiting: null, updated: false, dismissed: false };
  listeners.clear();
  reloadRequested = false;
  if (reloadTimer) clearTimeout(reloadTimer);
  reloadTimer = null;
}
